import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ALL_CATERING } from '../data/smaken';
import { ArrowMark } from './Marks';
export interface PartnerSummary {
  slug: string;
  name: string;
  city: string;
  categories: string[];
  occasions: string[];
  count: number;
}

/** Every place that actually delivers catering, with what they are good for. */
export function usePartnerSummaries(): PartnerSummary[] {
  return useMemo(() => {
    const map = new Map<string, PartnerSummary>();
    ALL_CATERING.forEach(offer => {
      const existing = map.get(offer.placeSlug);
      if (existing) {
        if (!existing.categories.includes(offer.category)) existing.categories.push(offer.category);
        offer.occasions.forEach(o => {
          if (!existing.occasions.includes(o)) existing.occasions.push(o);
        });
        existing.count += 1;
        return;
      }
      map.set(offer.placeSlug, {
        slug: offer.placeSlug,
        name: offer.placeName,
        city: offer.placeCity,
        categories: [offer.category],
        occasions: [...offer.occasions],
        count: 1
      });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }, []);
}
interface SelectProps {
  partners: PartnerSummary[];
  selected: string;
  onSelect: (slug: string) => void;
}

/**
 * Compact partner picker. A dropdown rather than chips — twelve names as
 * buttons wrapped to three rows and pushed the actual results off screen.
 */
export function PartnerSelect({
  partners,
  selected,
  onSelect
}: SelectProps) {
  return <div data-id="element-254" data-mp="2ixfx5gi1wdoo">
      <label htmlFor="c-partner" className="mb-2 block text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-255" data-mp="2ixpnx6ax6ovf">
        Verksamhet
      </label>
      <select id="c-partner" value={selected} onChange={e => onSelect(e.target.value)} className="max-w-[15rem] cursor-pointer truncate border-b border-black/25 bg-transparent pb-2 font-display text-2xl outline-none transition-colors duration-150 ease-out focus:border-rod" data-id="element-256" data-mp="ojy8tr9tncsl">
        <option value="" data-id="element-257" data-mp="1qxw0nng1ftfd">Alla</option>
        {partners.map(p => <option key={p.slug} value={p.slug} data-id="element-258" data-mp="1qzpatcpv0vs4">
            {p.name}
          </option>)}
      </select>
    </div>;
}

/** What the chosen place is good for, shown above the results. */
export function PartnerDetail({
  partner
}: {
  partner: PartnerSummary;
}) {
  const rows: [string, string][] = [['Bra för', partner.occasions.join(' · ')], ['Levererar', partner.categories.join(' · ')], ['Sortiment', `${partner.count} alternativ`]];
  return <div className="mt-8 border-l-2 border-rod pl-5" data-id="element-259" data-mp="2okbjask7ax4i">
      <p className="font-display text-xl uppercase leading-tight" data-id="element-260" data-mp="2jli6l2cqna5e">
        {partner.name} <span className="text-black/40" data-id="element-261" data-mp="2xljh79it1vy2">· {partner.city}</span>
      </p>
      <dl className="mt-3 space-y-1.5 text-sm" data-id="element-262" data-mp="2xjq8zpsjn3ny">
        {rows.map(([label, value]) => <div key={label} className="flex gap-3" data-id="element-263" data-mp="2soj8sryhgh3s">
            <dt className="w-24 shrink-0 text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-264" data-mp="3c7zxz2u969lc">
              {label}
            </dt>
            <dd data-id="element-265" data-mp="3h6tfd6cnukqs">{value}</dd>
          </div>)}
      </dl>
      <Link to={`/plats/${partner.slug}`} className="group mt-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide2 text-rod transition-colors duration-150 ease-out hover:text-black" data-id="element-266" data-mp="338lb0ddaoh5l">
        Läs om dem
        <ArrowMark className="h-3 w-7 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-267" data-mp="2tg77pg6kj2hb" />
      </Link>
    </div>;
}